import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useGame } from '@/context/GameContext';
import { OAuthProviderIcon } from '@/components/auth/OAuthProviderIcon';
import { loginWithDiscord, loginWithGitHub, loginWithGoogle } from '@/lib/auth/session';
import { Navbar } from '@/components/layout/Navbar';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { MaterialIcon } from '@/components/ui/MaterialIcon';
import SEO from '@/components/layout/SEO';
import { PHASES } from '@/data/phases';
import { COURSES } from '@/data/courses';

const faqSchema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
        {
            '@type': 'Question',
            name: 'What is startzone.dev?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'startzone.dev is a gamified learning platform built for the next generation of developers. Level up your dev skills by earning XP, completing quests, and collecting badges.',
            },
        },
        {
            '@type': 'Question',
            name: 'How do I earn XP on startzone.dev?',
            acceptedAnswer: {
                '@type': 'Answer',
                text: 'Earn XP by completing learning modules, answering questions correctly, and maintaining daily login streaks.',
            },
        },
    ],
});

// Map layout positions for each phase index on the canvas
const PHASE_POSITIONS = [
    'top-[12%] left-[8%]',      // Phase 0 — top-left
    'top-[45%] left-[22%]',     // Phase 1 — mid-left
    'top-[68%] left-[44%]',     // Phase 2 — bottom-center
    'top-[30%] left-[52%]',     // Phase 3 — center
    'top-[12%] right-[12%]',    // Phase 4 — top-right
    'top-[60%] right-[8%]',     // Phase 5 — bottom-right
];

// Icon per phase
const PHASE_ICONS = ['terminal', 'wifi', 'code', 'layers', 'rocket_launch', 'auto_awesome'];

// Compute derived map stats from real data
const availableCourses = PHASES.flatMap(p => p.courses).filter(c => c.isAvailable);
const totalModules = availableCourses.reduce((sum, c) => sum + (COURSES[c.id]?.total_modules ?? 0), 0);
const totalXP = availableCourses.reduce((sum, c) => sum + (COURSES[c.id]?.xp_reward ?? 0), 0);
const activeZones = PHASES.filter(p => p.courses.some(c => c.isAvailable)).length;
const totalCourses = PHASES.reduce((sum, p) => sum + p.courses.length, 0);

export default function LandingPage() {
    const { user, loading, isMockMode, signInMock } = useAuth();
    const { globalStats } = useGame();
    const showAuthButtons = !loading && !user;
    const handleProviderLogin = (provider: 'github' | 'google' | 'discord') => {
        if (isMockMode) {
            signInMock();
            return;
        }

        if (provider === 'github') loginWithGitHub();
        if (provider === 'google') loginWithGoogle();
        if (provider === 'discord') loginWithDiscord();
    };

    return (
        <div className="relative min-h-screen w-full overflow-x-hidden bg-black text-white crt-flicker-page">
            <SEO schema={faqSchema} title="STARTZONE.DEV | THE QUEST MAP" />

            <style>{`
                /* CRT / HUD animations */
                .crt-flicker-page {
                    animation: crt-flicker 0.15s infinite;
                }
                @keyframes crt-flicker {
                    0%   { opacity: 0.97; }
                    50%  { opacity: 1; }
                    100% { opacity: 0.98; }
                }

                .crt-overlay {
                    background:
                        linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.1) 50%),
                        linear-gradient(90deg, rgba(255,0,0,0.03), rgba(0,255,0,0.01), rgba(0,0,255,0.03));
                    background-size: 100% 3px, 3px 100%;
                    pointer-events: none;
                }

                .scanline-band {
                    width: 100%;
                    height: 100px;
                    background: linear-gradient(0deg, rgba(0,0,0,0) 0%, rgba(0,207,252,0.05) 50%, rgba(0,0,0,0) 100%);
                    opacity: 0.12;
                    position: absolute;
                    bottom: 100%;
                    animation: scanline 8s linear infinite;
                }
                @keyframes scanline {
                    0%   { bottom: 100%; }
                    100% { bottom: -100px; }
                }

                /* Hero glitch */
                .glow-title {
                    text-shadow: 0 0 20px rgba(0,207,252,0.6), 0 0 40px rgba(0,207,252,0.2);
                }
                .glitch-effect:hover {
                    animation: glitch 0.3s cubic-bezier(.25,.46,.45,.94) both infinite;
                }
                @keyframes glitch {
                    0%   { transform: translate(0); }
                    20%  { transform: translate(-2px, 2px); }
                    40%  { transform: translate(-2px, -2px); }
                    60%  { transform: translate(2px, 2px); }
                    80%  { transform: translate(2px, -2px); }
                    100% { transform: translate(0); }
                }

                /* Quest map */
                .path-dash {
                    stroke-dasharray: 10;
                    animation: dash 20s linear infinite;
                }
                @keyframes dash {
                    to { stroke-dashoffset: -1000; }
                }
                .map-pulse {
                    animation: pulse-ring 2s cubic-bezier(0.4,0,0.6,1) infinite;
                }
                @keyframes pulse-ring {
                    0%, 100% { opacity: 0.5; transform: scale(1); }
                    50%       { opacity: 1;   transform: scale(1.1); }
                }

                /* Footer fancy logo */
                .fancy-logo {
                    position: relative;
                    display: inline-block;
                    color: #fff;
                    letter-spacing: 0.15em;
                    text-shadow:
                        0.05em 0 0 rgba(255,0,255,0.75),
                        -0.025em -0.05em 0 rgba(0,255,255,0.75),
                        0.025em 0.05em 0 rgba(255,255,0,0.75);
                    animation: logo-glitch 500ms infinite;
                }
                .fancy-logo::before,
                .fancy-logo::after {
                    content: attr(data-text);
                    position: absolute;
                    top: 0; left: 0;
                    width: 100%; height: 100%;
                    opacity: 0.8;
                }
                .fancy-logo::before {
                    animation: glitch-layers 650ms infinite;
                    color: #00ffff;
                    z-index: -1;
                }
                .fancy-logo::after {
                    animation: glitch-layers 375ms infinite;
                    color: #ff00ff;
                    z-index: -2;
                }
                @keyframes logo-glitch {
                    0%   { text-shadow: 0.05em 0 0 rgba(255,0,255,.75), -0.025em -0.05em 0 rgba(0,255,255,.75), 0.025em 0.05em 0 rgba(255,255,0,.75); }
                    14%  { text-shadow: 0.05em 0 0 rgba(255,0,255,.75), -0.025em -0.05em 0 rgba(0,255,255,.75), 0.025em 0.05em 0 rgba(255,255,0,.75); }
                    15%  { text-shadow: -0.05em -0.025em 0 rgba(255,0,255,.75), 0.025em 0.025em 0 rgba(0,255,255,.75), -0.05em -0.05em 0 rgba(255,255,0,.75); }
                    49%  { text-shadow: -0.05em -0.025em 0 rgba(255,0,255,.75), 0.025em 0.025em 0 rgba(0,255,255,.75), -0.05em -0.05em 0 rgba(255,255,0,.75); }
                    50%  { text-shadow: 0.025em 0.05em 0 rgba(255,0,255,.75), 0.05em 0 0 rgba(0,255,255,.75), 0 -0.05em 0 rgba(255,255,0,.75); }
                    99%  { text-shadow: 0.025em 0.05em 0 rgba(255,0,255,.75), 0.05em 0 0 rgba(0,255,255,.75), 0 -0.05em 0 rgba(255,255,0,.75); }
                    100% { text-shadow: -0.025em 0 0 rgba(255,0,255,.75), -0.025em -0.025em 0 rgba(0,255,255,.75), -0.025em -0.05em 0 rgba(255,255,0,.75); }
                }
                @keyframes glitch-layers {
                    0%   { transform: translate(0); }
                    20%  { transform: translate(-3px,  3px); }
                    40%  { transform: translate(-3px, -3px); }
                    60%  { transform: translate( 3px,  3px); }
                    80%  { transform: translate( 3px, -3px); }
                    100% { transform: translate(0); }
                }
                .logo-container {
                    background:
                        linear-gradient(rgba(18,16,16,0) 50%, rgba(0,0,0,0.25) 50%),
                        linear-gradient(90deg, rgba(255,0,0,0.06), rgba(0,255,0,0.02), rgba(0,0,255,0.06));
                    background-size: 100% 2px, 3px 100%;
                    padding: 4px 8px;
                    border: 1px solid rgba(0,207,252,0.3);
                    box-shadow: inset 0 0 15px rgba(0,207,252,0.2), 0 0 10px rgba(0,207,252,0.1);
                }
            `}</style>

            {/* CRT Overlay — fixed, full screen */}
            <div className="crt-overlay fixed inset-0 z-[60] opacity-50 pointer-events-none" />

            {/* Scanline band + corner HUD text */}
            <div className="fixed inset-0 z-[55] pointer-events-none overflow-hidden">
                <div className="scanline-band" />
                <div className="absolute top-20 left-6 font-mono text-[8px] text-[#8cacff]/40 uppercase tracking-[0.3em]">SYS_READY // V.0.5.0</div>
                <div className="absolute bottom-4 left-6 font-mono text-[8px] text-[#8cacff]/40 uppercase tracking-[0.3em]">LOC: GRID_ALPHA // SZ_HQ</div>
                <div className="absolute top-4 right-6 font-mono text-[8px] text-[#8cacff]/40 uppercase tracking-[0.3em]">PACKET_LOSS: 0.001%</div>
            </div>

            <Navbar />

            <main className="relative">
                {/* ── HERO ── */}
                <section className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4 pt-20 bg-black">
                    {/* Radial glow */}
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,207,252,0.1),transparent_70%)]" />
                    {/* Crosshatch grid */}
                    <div
                        className="pointer-events-none absolute inset-0 opacity-20"
                        style={{
                            backgroundImage: 'linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)',
                            backgroundSize: '50px 50px',
                        }}
                    />

                    <div className="z-10 max-w-6xl w-full px-4 text-center">
                        {/* Badge */}
                        <div className="mb-6 flex items-center justify-center gap-4">
                            <div className="h-[2px] w-20 bg-gradient-to-r from-transparent to-[#00cffc]/50" />
                            <span className="font-['Space_Grotesk'] text-[#00cffc] tracking-[0.5em] text-[10px] font-black uppercase">
                                Operation: Genesis // System Online
                            </span>
                            <div className="h-[2px] w-20 bg-gradient-to-l from-transparent to-[#00cffc]/50" />
                        </div>

                        {/* H1 */}
                        <h1 className="mb-8 font-['Space_Grotesk'] font-[900] italic uppercase leading-[0.85] tracking-tighter select-none text-7xl md:text-[140px]">
                            <span className="text-white block">ARCHITECT</span>
                            <span className="text-[#00cffc] glow-title glitch-effect block">YOUR DESTINY</span>
                        </h1>

                        <p className="mx-auto mb-12 max-w-2xl text-lg leading-relaxed tracking-wide text-[#acabaa] md:text-xl">
                            Level up your dev skills, conquer learning territories, and unlock your status as an{' '}
                            <span className="text-white font-bold">Elite Developer</span> in the most immersive coding RPG ever built.
                        </p>

                        {showAuthButtons && (
                            <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto px-4">
                                {/* GitHub */}
                                <button
                                    type="button"
                                    onClick={() => handleProviderLogin('github')}
                                    aria-label="Sign in with GitHub"
                                    className="group relative h-14 w-full sm:w-48 bg-white/5 border border-white/10 hover:border-[#00cffc]/50 hover:bg-[#00cffc]/10 transition-all flex items-center justify-center gap-3"
                                >
                                    <OAuthProviderIcon provider="github" className="h-5 w-5 fill-white" />
                                    <span className="font-['Space_Grotesk'] font-black uppercase tracking-widest text-[11px]">GitHub</span>
                                </button>

                                {/* Google */}
                                <button
                                    type="button"
                                    onClick={() => handleProviderLogin('google')}
                                    aria-label="Sign in with Google"
                                    className="group relative h-14 w-full sm:w-48 bg-white/5 border border-white/10 hover:border-[#00cffc]/50 hover:bg-[#00cffc]/10 transition-all flex items-center justify-center gap-3"
                                >
                                    <OAuthProviderIcon provider="google" className="h-5 w-5" />
                                    <span className="font-['Space_Grotesk'] font-black uppercase tracking-widest text-[11px]">Google</span>
                                </button>

                                {/* Discord */}
                                <button
                                    type="button"
                                    onClick={() => handleProviderLogin('discord')}
                                    aria-label="Sign in with Discord"
                                    className="group relative h-14 w-full sm:w-48 bg-white/5 border border-white/10 hover:border-[#00cffc]/50 hover:bg-[#00cffc]/10 transition-all flex items-center justify-center gap-3"
                                >
                                    <OAuthProviderIcon provider="discord" className="h-5 w-5 fill-[#5865F2]" />
                                    <span className="font-['Space_Grotesk'] font-black uppercase tracking-widest text-[11px]">Discord</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Scroll indicator */}
                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 group cursor-pointer">
                        <span className="text-[9px] font-black tracking-[0.5em] uppercase text-[#00cffc]/60 group-hover:text-[#00cffc] transition-colors">
                            Load Map
                        </span>
                        <div className="w-[1px] h-12 bg-gradient-to-b from-[#00cffc] to-transparent" />
                    </div>
                </section>

                {/* ── QUEST MAP ── */}
                <section className="relative min-h-screen overflow-hidden bg-black px-6 py-24 border-t border-[#00cffc]/10">
                    <div className="relative z-10 mx-auto mb-16 max-w-7xl flex justify-between items-end">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-3">
                                <div className="w-1 h-6 bg-[#00cffc]" />
                                <h2 className="font-['Space_Grotesk'] text-5xl font-[900] italic uppercase tracking-tight">
                                    THE <span className="text-[#00cffc]">QUEST MAP</span>
                                </h2>
                            </div>
                            <p className="font-['Inter'] text-[#767575] uppercase text-[10px] tracking-[0.2em] ml-4">
                                Mission Grid Alpha // Choose Your Territory
                            </p>
                        </div>
                        <div className="hidden lg:flex gap-8 font-mono text-[10px] text-[#00cffc]/40">
                            <div>ZONES: <span className="text-[#00cffc]">{activeZones} ACTIVE</span></div>
                            <div>QUESTS: <span className="text-[#00cffc]">{totalModules}</span></div>
                            <div>XP POOL: <span className="text-[#00cffc]">{(totalXP / 1000).toFixed(1)}K</span></div>
                        </div>
                    </div>

                    {/* Map Canvas */}
                    <div className="relative w-full aspect-[21/9] min-h-[650px] border border-[#00cffc]/20 bg-[#050505] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,1)]">
                        {/* Grid dots */}
                        <div
                            className="absolute inset-0 opacity-20 pointer-events-none"
                            style={{
                                backgroundImage: 'radial-gradient(circle, #333 1.5px, transparent 1.5px)',
                                backgroundSize: '30px 30px',
                            }}
                        />

                        {/* Animated path lines */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none">
                            <defs>
                                <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#8cacff" stopOpacity="0.1" />
                                    <stop offset="50%" stopColor="#00cffc" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#00cffc" stopOpacity="0.1" />
                                </linearGradient>
                            </defs>
                            <path
                                className="path-dash"
                                d="M250 220 L480 500"
                                stroke="url(#lineGrad)"
                                strokeDasharray="10 10"
                                strokeWidth="2"
                            />
                            <path
                                d="M480 500 L850 350"
                                stroke="#444"
                                strokeDasharray="8 8"
                                strokeWidth="1.5"
                            />
                        </svg>

                        {/* Phase territory nodes — driven by real PHASES data */}
                        {PHASES.map((phase, idx) => {
                            const isUnlocked = phase.courses.some(c => c.isAvailable);
                            const courseCount = phase.courses.filter(c => c.isAvailable).length;
                            const totalPhase = phase.courses.length;
                            const position = PHASE_POSITIONS[idx] ?? 'top-[50%] left-[50%]';
                            const icon = PHASE_ICONS[idx] ?? 'explore';
                            return (
                                <Link
                                    key={phase.number}
                                    to="/grind"
                                    className={`absolute ${position} group z-20 ${isUnlocked ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                                >
                                    <div className="relative">
                                        {/* Glow */}
                                        {isUnlocked && (
                                            <div
                                                className="absolute -inset-8 blur-3xl rounded-full scale-150 map-pulse opacity-30"
                                                style={{ background: phase.color }}
                                            />
                                        )}

                                        {/* Diamond */}
                                        <div
                                            className={`relative flex items-center justify-center rotate-45 w-12 h-12 border-2 transition-all ${
                                                isUnlocked
                                                    ? 'group-hover:scale-110'
                                                    : 'grayscale opacity-40'
                                            }`}
                                            style={{
                                                borderColor: isUnlocked ? phase.color : '#444',
                                                background: isUnlocked ? `${phase.color}18` : '#0a0a0a',
                                                boxShadow: isUnlocked ? `0 0 12px ${phase.color}44` : 'none',
                                            }}
                                        >
                                            <MaterialIcon
                                                icon={icon}
                                                size={18}
                                                className="-rotate-45"
                                                style={{ color: isUnlocked ? phase.color : '#555' }}
                                            />
                                        </div>

                                        {/* Label */}
                                        <div className={`absolute top-full mt-5 left-1/2 -translate-x-1/2 whitespace-nowrap ${
                                            isUnlocked ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'
                                        }`}>
                                            <div
                                                className="p-2 border-l-2 bg-black/90 backdrop-blur-md min-w-[120px]"
                                                style={{ borderColor: isUnlocked ? phase.color : '#444' }}
                                            >
                                                <span className="block font-['Space_Grotesk'] font-black text-[10px] uppercase tracking-tight text-white">
                                                    Phase {phase.number}
                                                </span>
                                                <span className="block font-['Space_Grotesk'] text-[9px] font-bold uppercase tracking-tight" style={{ color: phase.color }}>
                                                    {phase.name}
                                                </span>
                                                <span className="block font-mono text-[8px] mt-1" style={{ color: isUnlocked ? phase.color : '#555' }}>
                                                    {isUnlocked ? `${courseCount}/${totalPhase} UNLOCKED` : 'ENCRYPTED'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}

                        {/* HUD: Global Progress (top-right) — computed from real PHASES data */}
                        {(() => {
                            const unlockedCount = availableCourses.length;
                            const pct = Math.round((unlockedCount / totalCourses) * 100);
                            return (
                                <div className="absolute top-6 right-6 w-56 bg-black/80 backdrop-blur-xl border border-white/10 p-4 font-mono">
                                    <div className="flex justify-between items-center mb-4">
                                        <span className="text-[9px] text-[#00cffc] tracking-widest uppercase">Content Live</span>
                                        <span className="text-[10px] text-white">{pct}%</span>
                                    </div>
                                    <div className="w-full h-[2px] bg-white/5 mb-6">
                                        <div
                                            className="h-full bg-[#00cffc] shadow-[0_0_8px_rgba(0,207,252,0.8)]"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 bg-[#8cacff]" />
                                                <span className="text-[8px] text-[#767575] uppercase">Courses Open</span>
                                            </div>
                                            <span className="text-[8px] text-white">{unlockedCount}/{totalCourses}</span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <div className="w-1.5 h-1.5 bg-[#4ade80]" />
                                                <span className="text-[8px] text-[#767575] uppercase">Active Zones</span>
                                            </div>
                                            <span className="text-[8px] text-white">{String(activeZones).padStart(2, '0')}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* HUD: Coordinates (bottom-left) */}
                        <div className="absolute bottom-6 left-6 flex items-center gap-4 text-[9px] font-mono text-white/20">
                            <div className="flex flex-col">
                                <span>X: 142.09</span>
                                <span>Y: 88.33</span>
                            </div>
                            <div className="w-px h-8 bg-white/10" />
                            <div className="flex flex-col uppercase">
                                <span>Sector: 07-G</span>
                                <span>Region: NA_EAST</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── STATS ── */}
                <section className="bg-black px-6 py-24 border-t border-white/5 relative">
                    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 lg:grid-cols-4 relative z-10">
                        {/* Main metric card */}
                        <div className="group lg:col-span-2 bg-white/5 p-10 relative overflow-hidden hover:bg-white/10 transition-all border border-white/5">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-[#00cffc]/5 blur-3xl group-hover:bg-[#00cffc]/10" />
                            <span className="mb-8 block font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.5em] text-[#8cacff]">
                                // METRICS_DASHBOARD
                            </span>
                            <h3 className="font-['Space_Grotesk'] text-5xl font-black uppercase leading-[0.9] mb-12">
                                ELITE{' '}
                                <span className="text-white">OPERATIVE</span>
                                <br />
                                <span className="text-[#d674ff] glow-title">STATISTICS</span>
                            </h3>
                            <div className="flex items-end justify-between border-t border-white/10 pt-8">
                                <div>
                                    <span className="block font-['Space_Grotesk'] text-5xl font-black text-white italic tracking-tighter">
                                        {globalStats?.players != null
                                            ? globalStats.players >= 1000
                                                ? `${(globalStats.players / 1000).toFixed(1)}K`
                                                : String(globalStats.players)
                                            : '—'}
                                    </span>
                                    <span className="block font-mono text-[9px] uppercase tracking-widest text-[#767575] mt-1">
                                        Operatives Enrolled
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="block font-['Space_Grotesk'] text-5xl font-black text-white italic tracking-tighter">{totalModules}</span>
                                    <span className="block font-mono text-[9px] uppercase tracking-widest text-[#767575] mt-1">
                                        Quests Available
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Armory card */}
                        <div className="group bg-white/5 p-10 border-t-4 border-[#00cffc] hover:bg-[#00cffc]/10 transition-all cursor-pointer border border-white/5">
                            <div className="w-12 h-12 flex items-center justify-center border border-[#00cffc] mb-8 group-hover:bg-[#00cffc] group-hover:text-black transition-all">
                                <MaterialIcon icon="military_tech" size={28} className="text-current text-[#00cffc] group-hover:text-black transition-colors" />
                            </div>
                            <h4 className="mb-4 font-['Space_Grotesk'] text-xl font-black uppercase tracking-tight text-white">
                                Armory Access
                            </h4>
                            <p className="font-['Inter'] text-sm leading-relaxed text-[#acabaa] opacity-70">
                                Unlock legendary syntax skins and IDE components. High rarity items drop from Boss Territories.
                            </p>
                        </div>

                        {/* Virtual HUD card */}
                        <div className="group bg-white/5 p-10 border-t-4 border-[#d674ff] hover:bg-[#d674ff]/10 transition-all cursor-pointer border border-white/5">
                            <div className="w-12 h-12 flex items-center justify-center border border-[#d674ff] mb-8 group-hover:bg-[#d674ff] group-hover:text-black transition-all">
                                <MaterialIcon icon="terminal" size={28} fill className="text-current text-[#d674ff] group-hover:text-black transition-colors" />
                            </div>
                            <h4 className="mb-4 font-['Space_Grotesk'] text-xl font-black uppercase tracking-tight text-white">
                                Virtual HUD
                            </h4>
                            <p className="font-['Inter'] text-sm leading-relaxed text-[#acabaa] opacity-70">
                                Track your XP, streaks, and skill progress through a real-time learning HUD built directly into every quest.
                            </p>
                        </div>
                    </div>
                </section>

                <SiteFooter />

                {/* Mobile bottom nav */}
                <nav className="fixed bottom-0 left-0 z-50 flex h-16 w-full items-center justify-around border-t border-[#00cffc]/20 bg-black/90 shadow-[0_-4px_24px_rgba(0,0,0,0.8)] backdrop-blur-2xl md:hidden">
                    <div className="flex flex-col items-center justify-center text-[#00cffc] drop-shadow-[0_0_8px_rgba(0,207,252,0.5)] transition-transform active:scale-90">
                        <MaterialIcon icon="home" size={22} fill className="text-current" />
                        <span className="font-['Space_Grotesk'] text-[9px] font-black uppercase mt-1">Base</span>
                    </div>
                    <Link to="/grind" className="flex flex-col items-center justify-center text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90">
                        <MaterialIcon icon="map" size={22} className="text-current" />
                        <span className="font-['Space_Grotesk'] text-[9px] font-black uppercase mt-1">Quest Map</span>
                    </Link>
                    <Link to="/armory" className="flex flex-col items-center justify-center text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90">
                        <MaterialIcon icon="inventory_2" size={22} className="text-current" />
                        <span className="font-['Space_Grotesk'] text-[9px] font-black uppercase mt-1">Loot</span>
                    </Link>
                    <Link to="/hall-of-fame" className="flex flex-col items-center justify-center text-[#484848] transition-transform hover:text-[#8cacff] active:scale-90">
                        <MaterialIcon icon="leaderboard" size={22} className="text-current" />
                        <span className="font-['Space_Grotesk'] text-[9px] font-black uppercase mt-1">Rank</span>
                    </Link>
                </nav>
            </main>
        </div>
    );
}
